import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, X, ChevronDown, ChevronRight, Layers } from 'lucide-react';
import clsx from 'clsx';
import { fetchTags, createTag, deleteTag, type TagDefinition, type TagScope } from '@/db/tags';
import { handleError } from '@/utils/errorHandler';
import { clampToViewport } from '@/hooks/useDropdownPosition';

const COLOR_OPTIONS = [
  { value: 'accent-violet', hex: '#8b5cf6', label: 'Viola' },
  { value: 'accent-blue',   hex: '#3b82f6', label: 'Blu' },
  { value: 'accent-emerald',hex: '#10b981', label: 'Verde' },
  { value: 'accent-rose',   hex: '#f43f5e', label: 'Rosa' },
  { value: 'accent-amber',  hex: '#f59e0b', label: 'Ambra' },
  { value: 'accent-cyan',   hex: '#06b6d4', label: 'Ciano' },
  { value: 'text-quaternary',hex: '#6b7280', label: 'Grigio' },
];

function hexFor(color?: string | null) {
  return COLOR_OPTIONS.find((c) => c.value === color)?.hex ?? '#6b7280';
}

// ─── Inherited tags section ────────────────────────────────────────────────────

function InheritedTagsSection({
  scope,
  scopeId,
  label,
}: {
  scope: TagScope;
  scopeId?: string;
  label: string;
}) {
  const [tags, setTags] = useState<TagDefinition[]>([]);
  const [collapsed, setCollapsed] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!scopeId) return;
    setLoading(true);
    fetchTags(scope, scopeId)
      .then(setTags)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [scope, scopeId]);

  if (!scopeId || loading || tags.length === 0) return null;

  return (
    <div className="mt-4 pt-4 border-t border-border-primary/50">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center gap-2 text-[11px] text-text-tertiary hover:text-text-secondary transition-colors w-full"
      >
        {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        <Layers className="w-3.5 h-3.5" />
        {label} ({tags.length})
      </button>
      {!collapsed && (
        <div className="mt-2 flex flex-wrap gap-1.5 opacity-60">
          {tags.map((t) => (
            <span
              key={t.id}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border border-border-primary/50"
              style={{ backgroundColor: hexFor(t.color) + '22', color: hexFor(t.color) }}
            >
              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: hexFor(t.color) }} />
              {t.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Tags manager ─────────────────────────────────────────────────────────────

interface TagsManagerProps {
  scope: TagScope;
  scopeId?: string;
  spaceId?: string;
  spaceName?: string;
  orgId?: string;
  orgName?: string;
}

export function TagsManager({ scope, scopeId, spaceId, spaceName, orgId, orgName }: TagsManagerProps) {
  const [tags, setTags] = useState<TagDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('accent-violet');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [pickerPos, setPickerPos] = useState({ top: 0, left: 0 });
  const colorBtnRef = useRef<HTMLButtonElement>(null);

  const load = useCallback(async () => {
    if (!scopeId) { setLoading(false); return; }
    setLoading(true);
    try {
      setTags(await fetchTags(scope, scopeId));
    } catch (e) {
      handleError(e, { level: 'warning', message: 'Errore caricamento tag' });
    } finally {
      setLoading(false);
    }
  }, [scope, scopeId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newName.trim() || !scopeId) return;
    try {
      const created = await createTag({
        name: newName.trim(),
        color: newColor,
        scope,
        scope_id: scopeId,
        order_index: tags.length,
        created_at: Date.now(),
      });
      setTags((prev) => [...prev, created]);
      setNewName('');
      setNewColor('accent-violet');
      setShowAddForm(false);
    } catch (e) {
      handleError(e, { level: 'error', message: 'Errore creazione tag' });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTag(id);
      setTags((prev) => prev.filter((t) => t.id !== id));
    } catch (e) {
      handleError(e, { level: 'error', message: 'Errore eliminazione tag' });
    }
  };

  const handleColorClick = () => {
    if (!showColorPicker && colorBtnRef.current) {
      const r = colorBtnRef.current.getBoundingClientRect();
      setPickerPos(clampToViewport(r.left, r.bottom + 4, 140, 120));
    }
    setShowColorPicker((v) => !v);
  };

  if (loading) return <div className="text-center py-8 text-[11px] text-text-quaternary">Caricamento…</div>;

  return (
    <div className="space-y-2">
      {tags.length === 0 && !showAddForm && (
        <p className="text-[11px] text-text-tertiary text-center py-6">
          Nessun tag. Aggiungine uno per categorizzare i task.
        </p>
      )}

      <div className="flex flex-wrap gap-1.5">
        {tags.map((tag) => {
          const hex = hexFor(tag.color);
          return (
            <span
              key={tag.id}
              className="group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors"
              style={{ backgroundColor: hex + '22', borderColor: hex + '44', color: hex }}
            >
              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: hex }} />
              {tag.name}
              <button
                onClick={() => handleDelete(tag.id)}
                className="ml-0.5 opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity"
                title="Elimina tag"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          );
        })}
      </div>

      {showAddForm && (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-accent-violet/30 bg-accent-violet/5 mt-2">
          <div>
            <button
              ref={colorBtnRef}
              onClick={handleColorClick}
              className="w-6 h-6 rounded-full border-2 hover:scale-110 transition-transform"
              style={{ backgroundColor: hexFor(newColor), borderColor: hexFor(newColor) + '88' }}
              title="Cambia colore"
            />
            {showColorPicker && (
              <div
                className="fixed z-[999] p-2 rounded-lg border border-border-primary bg-surface-primary shadow-xl"
                style={{ top: pickerPos.top, left: pickerPos.left }}
              >
                <div className="grid grid-cols-4 gap-1.5">
                  {COLOR_OPTIONS.map((c) => (
                    <button
                      key={c.value}
                      onClick={() => { setNewColor(c.value); setShowColorPicker(false); }}
                      className={clsx('w-5 h-5 rounded-full hover:scale-110 transition-transform', newColor === c.value && 'ring-2 ring-white ring-offset-1')}
                      style={{ backgroundColor: c.hex }}
                      title={c.label}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAdd();
              if (e.key === 'Escape') { setShowAddForm(false); setNewName(''); }
            }}
            placeholder="Nome tag…"
            autoFocus
            className="flex-1 px-2 py-1.5 rounded-lg bg-surface-tertiary border border-border-primary text-[12px] text-text-primary focus:border-accent-violet focus:ring-1 focus:ring-accent-violet"
          />
          <button
            onClick={handleAdd}
            disabled={!newName.trim()}
            className="px-3 py-1.5 rounded-lg bg-accent-violet text-white text-[11px] font-medium disabled:opacity-40 hover:bg-accent-violet/90"
          >
            Aggiungi
          </button>
          <button onClick={() => { setShowAddForm(false); setNewName(''); }} className="p-1.5 rounded hover:bg-surface-hover text-text-quaternary">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {!showAddForm && (
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-border-secondary text-[11px] text-text-tertiary hover:text-text-secondary hover:border-text-tertiary transition-all mt-1"
        >
          <Plus className="w-3.5 h-3.5" />
          Aggiungi tag
        </button>
      )}

      {(scope === 'list' || scope === 'folder') && spaceId && (
        <InheritedTagsSection scope="project" scopeId={spaceId} label={`Ereditati da ${spaceName || 'spazio'}`} />
      )}
      {scope !== 'organization' && orgId && (
        <InheritedTagsSection scope="organization" scopeId={orgId} label={`Ereditati da ${orgName || 'organizzazione'}`} />
      )}
    </div>
  );
}
